import "./surveys.css";
import { Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { getOurSideSurveyQuery } from "@src/util/query/survey.query";
import { submitOurSideSurveyMutation } from "@src/util/mutation/survey.mutation";
import { SurveyAnswerForm } from "./surveyAnswerForm";
import { onForm, SurveyFormFill } from "../formOnPdf/formOnPdf";

/**
 * Our side of a two-way survey, for one customer or supplier: the "Us" and "Both" questions.
 * Answered by the person (or anyone with the role) chosen when it was sent, or by a manager.
 * The customer / supplier never sees these answers.
 */
export const OurSideModal = ({ responseId, onClose, onAnswered = () => {} }) => {
    const dispatch = useDispatch();
    const { data, loading } = useQuery(getOurSideSurveyQuery, { variables: { responseId }, skip: !responseId, fetchPolicy: "network-only" });
    const [submit, { loading: submitting }] = useMutation(submitOurSideSurveyMutation);
    const form = data?.getOurSideSurvey?.response;

    const send = async (answers, signed) => {
        try {
            const res = await submit({ variables: { responseId, answers, ...(signed || {}) } });
            dispatch(setNotification({ error: false, notificationState: true, message: res?.data?.submitOurSideSurvey?.message || "Saved" }));
            onAnswered();
            onClose();
        } catch (err) {
            dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
        }
    };

    return (
        <Modal open={!!responseId} width={onForm(form) ? 960 : 700} closable={false} onCancel={onClose} footer={null}>
            <div className="modal-header-title">
                <span style={{ letterSpacing: "0.2px" }}>{form ? `${form.title} - our side for ${form.about}` : "Our side"}</span>
                <span onClick={onClose}><FontAwesomeIcon className="close" icon={faClose} /></span>
            </div>
            <div className="modal-body" style={{ margin: "16px 0 0", maxHeight: "70vh", overflowY: "auto" }}>
                {loading || !form ? <Spinner /> : form.state === "submitted" ? (
                    <div className="svy-state">
                        <div className="svy-state-title">Our side is done</div>
                        <div className="svy-state-text">Answered{form.submittedBy ? ` by ${form.submittedBy}` : ""} on {dayjs(form.submittedAt).format("D MMMM YYYY")}.</div>
                    </div>
                ) : form.state === "closed" ? (
                    <div className="svy-state"><div className="svy-state-title">This survey has closed</div></div>
                ) : (
                    <>
                        <div className="svy-note">
                            Our view of {form.about} - they won't see these answers. The results show both sides side by side. Please answer by {dayjs(form.closesOn).format("D MMMM YYYY")}.
                        </div>
                        {onForm(form)
                            ? <SurveyFormFill form={form} side="us" submitting={submitting} onSubmit={send} submitLabel="Save our side" />
                            : <SurveyAnswerForm form={form} submitting={submitting} onSubmit={send} submitLabel="Save our side" />}
                    </>
                )}
            </div>
        </Modal>
    );
};
